import React, { useState } from 'react';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  IconButton,
  Box,
  Chip,
  Collapse,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  School as SchoolIcon,
} from '@mui/icons-material';
import { subjectService } from '../services/subjectService';
import { useNavigate } from 'react-router-dom';

const SubjectCard = ({ subject, fetchSubjects }) => {
  const [expanded, setExpanded] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editData, setEditData] = useState({
    name: subject.name,
    description: subject.description || '',
  });
  const navigate = useNavigate();

  const handleExpandClick = () => {
    setExpanded(!expanded);
  };

  const handleEdit = (e) => {
    e.stopPropagation(); // Prevent card click when editing
    setEditData({
      name: subject.name,
      description: subject.description || '',
    });
    setEditDialogOpen(true);
  };

  const handleSaveEdit = () => {
    subjectService.updateSubject(subject.id, editData);
    setEditDialogOpen(false);
  };

  const handleDelete = (e) => {
    e.stopPropagation(); // Prevent card click when deleting
    if (window.confirm(`Are you sure you want to delete "${subject.name}"? This will also delete all topics and questions within it.`)) {
      subjectService.deleteSubject(subject.id)
        .then(() => fetchSubjects())
        .catch(console.log("Something went wrong"))
    }
  };

  const handleCardClick = () => {
    navigate(`/subject/${subject.id}/topics`);
  };

  const topicCount = subject.topics?.length || 0;
  const questionCount = subject.topics?.reduce((total, topic) => total + (topic.questions?.length || 0), 0) || 0;

  return (
    <>
      <Card
        onClick={handleCardClick}
        sx={{
          mb: 2,
          borderRadius: 3,
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
          border: '1px solid rgba(0, 0, 0, 0.05)',
          cursor: 'pointer',
          '&:hover': {
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
            transform: 'translateY(-2px)',
          },
          transition: 'all 0.2s ease-in-out',
        }}
      >
        <CardContent sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
            <Box sx={{ flexGrow: 1, mr: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <SchoolIcon sx={{ mr: 1, color: 'primary.main', fontSize: 24 }} />
                <Typography
                  variant="h5"
                  component="h2"
                  sx={{ fontWeight: 700, color: 'text.primary' }}
                  title="Click to view topics in this subject"
                >
                  {subject.name}
                </Typography>
              </Box>
              {subject.description && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.6 }}>
                  {subject.description}
                </Typography>
              )}
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip
                  label={`${topicCount} Topic${topicCount !== 1 ? 's' : ''}`}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
                <Chip
                  label={`${questionCount} Question${questionCount !== 1 ? 's' : ''}`}
                  size="small"
                  color="secondary"
                  variant="outlined"
                />
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Button
                variant="outlined"
                color="primary"
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/subject/${subject.id}/test`);
                }}
                sx={{ mr: 2, borderRadius: 2 }}
              >
                Take Test
              </Button>
              <IconButton
                size="small"
                onClick={handleEdit}
                sx={{ color: 'primary.main', mr: 1 }}
              >
                <EditIcon />
              </IconButton>
              <IconButton
                size="small"
                onClick={handleDelete}
                sx={{ color: 'error.main', mr: 1 }}
              >
                <DeleteIcon />
              </IconButton>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Edit Subject Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Subject</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Subject Name"
            fullWidth
            variant="outlined"
            value={editData.name}
            onChange={(e) => setEditData({ ...editData, name: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Description (Optional)"
            fullWidth
            variant="outlined"
            multiline
            rows={3}
            value={editData.description}
            onChange={(e) => setEditData({ ...editData, description: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={(e) => { e.stopPropagation(); setEditDialogOpen(false); }}>Cancel</Button>
          <Button onClick={(e) => { e.stopPropagation(); handleSaveEdit(); }} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SubjectCard;
